import { api } from './client'
import {
  loginResponseSchema,
  sessionDtoSchema,
  teacherDtoSchema,
  tokenResponseSchema,
  totpSetupResponseSchema,
  totpStatusSchema,
  totpVerifyResponseSchema,
  backupCodesResponseSchema,
  trustedDeviceSchema,
} from './schemas'
import type {
  TotpVerifyResponse,
  TrustedDeviceDto,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  SessionDto,
  TeacherDto,
  TokenResponse,
  TotpSetupResponse,
  TotpStatus,
} from './types'
import { z } from 'zod'

async function parsed<T>(p: Promise<unknown>, schema: { parse: (raw: unknown) => T }): Promise<T> {
  const raw = await p
  return schema.parse(raw)
}

export const authApi = {
  login: (body: LoginRequest): Promise<LoginResponse> =>
    parsed(api('/auth/login', { method: 'POST', body: JSON.stringify(body) }), loginResponseSchema),

  /** trustDevice — «доверять этому устройству 30 дней» (сервер ставит httpOnly-cookie). */
  loginTotp: (challengeToken: string, code: string, trustDevice = false): Promise<LoginResponse> =>
    parsed(
      api('/auth/login/totp', { method: 'POST', body: JSON.stringify({ challengeToken, code, trustDevice }) }),
      loginResponseSchema,
    ),

  register: (body: RegisterRequest): Promise<TokenResponse> =>
    parsed(api('/auth/register', { method: 'POST', body: JSON.stringify(body) }), tokenResponseSchema),

  me: (): Promise<TeacherDto> => parsed(api('/auth/me'), teacherDtoSchema),

  updateProfile: (
    body: Partial<Pick<TeacherDto, 'displayName' | 'locale' | 'theme' | 'preferredLlmProvider'>>,
  ): Promise<TeacherDto> =>
    parsed(api('/auth/me', { method: 'PUT', body: JSON.stringify(body) }), teacherDtoSchema),

  logout: (): Promise<{ ok: boolean }> =>
    api('/auth/logout', { method: 'POST' }) as Promise<{ ok: boolean }>,

  // ── 2FA TOTP ─────────────────────────────────────────────────────────
  totpStatus: (): Promise<TotpStatus> =>
    parsed(api('/auth/totp/status'), totpStatusSchema),

  totpSetup: (): Promise<TotpSetupResponse> =>
    parsed(api('/auth/totp/setup', { method: 'POST' }), totpSetupResponseSchema),

  /** С ограниченным токеном настройки ответ содержит teacher — сессия уже выдана (cookie). */
  totpVerify: (code: string): Promise<TotpVerifyResponse> =>
    parsed(api('/auth/totp/verify', { method: 'POST', body: JSON.stringify({ code }) }), totpVerifyResponseSchema),

  /** Перевыпуск 10 резервных кодов (старые аннулируются). */
  regenerateBackupCodes: (code: string): Promise<string[]> =>
    parsed(
      api('/auth/totp/backup-codes', { method: 'POST', body: JSON.stringify({ code }) }),
      backupCodesResponseSchema,
    ).then((r) => r.backupCodes),

  // ── Доверенные устройства ─────────────────────────────────────────────
  listTrustedDevices: (): Promise<TrustedDeviceDto[]> =>
    parsed(api('/auth/trusted-devices'), z.array(trustedDeviceSchema)),

  revokeTrustedDevice: (id: string): Promise<void> =>
    api(`/auth/trusted-devices/${id}`, { method: 'DELETE' }).then(() => undefined),

  revokeAllTrustedDevices: (): Promise<{ revoked: number }> =>
    api('/auth/trusted-devices', { method: 'DELETE' }) as Promise<{ revoked: number }>,

  totpDisable: (code: string): Promise<void> =>
    api('/auth/totp/disable', { method: 'POST', body: JSON.stringify({ code }) }).then(() => undefined),

  // ── Активные сессии ───────────────────────────────────────────────────
  listSessions: (): Promise<SessionDto[]> =>
    parsed(api('/auth/sessions'), z.array(sessionDtoSchema)),

  revokeSession: (sessionId: string): Promise<void> =>
    api(`/auth/sessions/${sessionId}`, { method: 'DELETE' }).then(() => undefined),

  revokeAllOtherSessions: (): Promise<{ revoked: number }> =>
    api('/auth/sessions/all-others', { method: 'DELETE' }) as Promise<{ revoked: number }>,
}
